import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import Markdown from 'markdown-to-jsx'
import Chip from 'components/atoms/Chip'
import Beacon from 'components/atoms/Beacon'
import { useHistory } from 'react-router-dom'
import '@stretto/cds-tokens/lib/css/base.css'
import './styles.scss'
import { Account } from 'share/domain/account'

/**
 * Collapsible list component that shows only first 5 items by default
 */
interface CollapsibleListProps {
  children?: ReactNode
  ordered: boolean
  className?: string
  [key: string]: unknown
}

const CollapsibleList = ({
  children,
  ordered,
  ...props
}: CollapsibleListProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false)
  const listItems = React.Children.toArray(children)
  const hasMoreThan5Items = listItems.length > 5

  // If 5 or fewer items, render normally
  if (!hasMoreThan5Items) {
    return React.createElement(ordered ? 'ol' : 'ul', props, children)
  }

  // Show first 5 items or all items based on expanded state
  const visibleItems = isExpanded ? listItems : listItems.slice(0, 5)

  return (
    <div className='collapsible-list-container'>
      {React.createElement(
        ordered ? 'ol' : 'ul',
        {
          ...props,
          className: `${props.className || ''} collapsible-list`.trim()
        },
        visibleItems
      )}
      <button
        className='collapsible-list-toggle'
        onClick={() => setIsExpanded(!isExpanded)}
        type='button'
        aria-expanded={isExpanded}
        aria-label={isExpanded ? 'Show fewer items' : 'Show more items'}
      >
        {isExpanded ? 'Show me less details' : 'Show me the details'}
      </button>
    </div>
  )
}

interface ChatMessageProps {
  type: 'user' | 'bot' | 'system'
  message?: string
  documents?: Array<{
    fileName: string
    documentId: string
  }>
  userId?: string
  children?: ReactNode
  /** Optional transient status for bot bubble */
  status?: 'thinking' | 'processing' | 'idle'
  /** Enable progressive typing animation for bot during generating */
  animateTyping?: boolean
  /** Approx ms per character when animating */
  typingDelayMs?: number
  /** Optional callback fired as content grows (useful to keep view pinned to bottom) */
  onProgress?: () => void
  account: Account | null
}

const ChatMessage = ({
  type,
  message,
  documents,
  userId,
  children,
  status,
  animateTyping = true,
  typingDelayMs = 20,
  onProgress,
  account
}: ChatMessageProps): JSX.Element => {
  const history = useHistory()
  const baseClass = ['ChatMessage', type].join(' ')
  const fullText = useMemo(() => {
    const raw = message ?? ''
    // Convert escaped newlines to real newlines, then make single newlines render as line breaks
    // by adding two spaces before each newline (Markdown line break syntax)
    return raw
      .replace(/\r\n/g, '\n') // Normalize line endings
      .replace(/\\n/g, '\n') // Convert escaped newlines
      .replace(/\n/g, '  \n') // Add two spaces before newlines for Markdown line breaks
  }, [message])

  // Typewriter state for bot messages
  const [displayed, setDisplayed] = useState<string>('')
  const timerRef = useRef<number | null>(null)
  const [initials, setInitials] = useState<string>(
    `${account?.givenName?.[0]}${account?.familyName?.[0]}`
  )

  useEffect(() => {
    if (type !== 'bot') {
      setDisplayed(fullText)
      return
    }
    // If animation disabled, always show full text
    if (!animateTyping) {
      setDisplayed(fullText)
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }

    // During thinking: pause animation only if there's no content to show
    if (status === 'thinking' && fullText.length === 0) {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }

    // During processing: show content immediately without animation
    if (status === 'processing') {
      setDisplayed(fullText)
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }

    const target = fullText

    // If idle but we haven't finished, continue animating to completion
    const shouldAnimate = displayed.length < target.length
    if (!shouldAnimate) {
      // Finished; ensure full text and clear timer
      setDisplayed(target)
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
      return
    }

    // Start or restart typing interval
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    timerRef.current = window.setInterval(() => {
      setDisplayed((prev) => {
        if (prev.length >= target.length) {
          if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
          }
          return prev
        }
        const next = target.slice(0, prev.length + 1)
        // notify parent to keep scrolled to bottom while typing
        onProgress?.()
        return next
      })
    }, Math.max(typingDelayMs, 5))

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [type, fullText, status, animateTyping, typingDelayMs, displayed.length])

  useEffect(() => {
    setInitials(`${account?.givenName?.[0]}${account?.familyName?.[0]}`)
  }, [account?.givenName, account?.familyName])

  const textToRender = type === 'bot' ? displayed : fullText

  // Don't render if there's no message content and no children and no documents, unless it's a thinking state
  if (
    !textToRender.trim() &&
    !children &&
    (!documents || documents.length === 0) &&
    status !== 'thinking'
  ) {
    return <></>
  }

  return (
    <>
      <li className={baseClass} data-type={type} data-userid={userId}>
        {type === 'bot' && (
          <span className='icon'>
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M8.08175 0.669759C8.24391 0.683003 8.40025 0.735978 8.53747 0.823405L8.60453 0.869629L8.66768 0.921712C8.78885 1.03027 8.8814 1.16693 8.93787 1.3195L8.96326 1.39697L8.96716 1.40999L10.0212 5.50049H10.0205C10.0503 5.61575 10.1108 5.721 10.195 5.80518C10.2792 5.88932 10.3845 5.94924 10.4997 5.979L14.5896 7.03304L14.6006 7.0363C14.7852 7.08722 14.9505 7.18976 15.0778 7.33122L15.1306 7.39437L15.1774 7.46143C15.2799 7.6217 15.335 7.8085 15.335 7.99984C15.335 8.21868 15.2631 8.43184 15.1306 8.60596C14.998 8.78009 14.8116 8.90583 14.6006 8.96403C14.597 8.96503 14.5932 8.9657 14.5896 8.96663L10.4997 10.0207C10.3844 10.0504 10.2792 10.1109 10.195 10.1952C10.111 10.2792 10.0504 10.3841 10.0205 10.4992L10.0212 10.4998L8.96651 14.5897C8.9654 14.594 8.96381 14.5984 8.9626 14.6027C8.90379 14.8129 8.77775 14.9982 8.60388 15.13C8.43002 15.2619 8.21793 15.3338 7.99971 15.3338C7.7815 15.3338 7.56941 15.2619 7.39555 15.13C7.22168 14.9982 7.09564 14.8129 7.03682 14.6027C7.03561 14.5984 7.03404 14.594 7.03292 14.5897L5.97888 10.4998C5.94912 10.3846 5.88922 10.2794 5.80505 10.1952C5.74181 10.1319 5.66667 10.0821 5.58435 10.0487L5.50037 10.0207L1.41052 8.96598C1.4054 8.96466 1.39998 8.96352 1.3949 8.96208C1.18557 8.90266 1.00135 8.77631 0.870157 8.6027C0.755352 8.45078 0.68631 8.2699 0.670938 8.08122L0.667683 7.99984L0.670938 7.91846C0.686363 7.72989 0.755419 7.54881 0.870157 7.39697L0.921589 7.33447C1.04761 7.19319 1.2117 7.09025 1.3949 7.03825L1.40987 7.03369L5.50037 5.97835L5.58435 5.95036C5.66662 5.91698 5.74183 5.8677 5.80505 5.80452C5.88911 5.72053 5.949 5.61549 5.97888 5.50049L7.03357 1.40999L7.03747 1.39697C7.0963 1.18684 7.22234 1.00151 7.3962 0.869629L7.4626 0.823405C7.62277 0.721251 7.80928 0.666504 8.00037 0.666504L8.08175 0.669759ZM1.99971 12.6665C1.63166 12.6664 1.33305 12.3679 1.33305 11.9998C1.33313 11.6547 1.59582 11.3711 1.93201 11.3371L1.99971 11.3332C1.9998 10.9652 2.29838 10.6667 2.66638 10.6665C3.01165 10.6665 3.29578 10.9292 3.32979 11.2655L3.33305 11.3332L3.40141 11.3371C3.73755 11.3712 3.99963 11.6547 3.99971 11.9998C3.99971 12.368 3.70124 12.6665 3.33305 12.6665C3.33305 13.0347 3.03457 13.3332 2.66638 13.3332C2.29832 13.333 1.99971 13.0346 1.99971 12.6665ZM7.2699 5.83317V5.83382C7.18044 6.17959 7.0004 6.49542 6.74776 6.74788C6.52665 6.96884 6.25693 7.13391 5.9613 7.23161L5.83305 7.26937L3.00232 7.99919L5.83305 8.72966L5.9613 8.76742C6.25713 8.8652 6.52658 9.03126 6.74776 9.25244C7.00034 9.50506 7.1806 9.82062 7.2699 10.1665L7.27055 10.1672L7.99906 12.9972L8.72953 10.1672V10.1665C8.81882 9.82054 8.99968 9.5051 9.25232 9.25244C9.50504 8.99972 9.82096 8.81893 10.167 8.72966L12.9984 7.99984L10.167 7.27002C9.82101 7.18075 9.50502 7.00055 9.25232 6.74788C9.03115 6.52672 8.86508 6.25722 8.76729 5.96143L8.72953 5.83317L7.99971 3.00179L7.2699 5.83317ZM12.6664 4.6665V3.99984H11.9997C11.6317 3.99968 11.333 3.70127 11.333 3.33317C11.3331 2.96515 11.6317 2.66666 11.9997 2.6665H12.6664V1.99984C12.6665 1.63182 12.965 1.33333 13.333 1.33317C13.7012 1.33317 13.9996 1.63172 13.9997 1.99984V2.6665H14.6664C15.0345 2.6665 15.333 2.96506 15.333 3.33317C15.333 3.70136 15.0346 3.99984 14.6664 3.99984H13.9997V4.6665C13.9997 5.03469 13.7012 5.33317 13.333 5.33317C12.965 5.33302 12.6664 5.0346 12.6664 4.6665Z'
                fill='#F4F6F6'
              />
            </svg>
          </span>
        )}
        <div className='message'>
          {type === 'bot' && status && status !== 'idle' && (
            <div className='message-status' aria-live='polite'>
              <Beacon state={status} />
            </div>
          )}
          {children ? (
            children
          ) : (
            <Markdown
              options={{
                forceBlock: true,
                wrapper: 'div',
                overrides: {
                  a: {
                    component: function MarkdownLink(
                      props: React.ComponentProps<'a'>
                    ) {
                      // Handle internal app routes
                      if (props.href?.startsWith('/')) {
                        return (
                          <a
                            {...props}
                            onClick={(e) => {
                              e.preventDefault()
                              // Navigate using React Router
                              history.push(props.href || '/')
                            }}
                            style={{ cursor: 'pointer', color: '#007bff' }}
                          />
                        )
                      }
                      // Handle hash links (scroll to element)
                      if (props.href?.startsWith('#')) {
                        return (
                          <a
                            {...props}
                            onClick={(e) => {
                              e.preventDefault()
                              // Handle internal navigation
                              const targetId = props.href?.substring(1)
                              if (targetId) {
                                const element =
                                  document.getElementById(targetId)
                                if (element) {
                                  element.scrollIntoView({ behavior: 'smooth' })
                                }
                              }
                            }}
                            style={{ cursor: 'pointer', color: '#007bff' }}
                          />
                        )
                      }
                      // Handle external links
                      return (
                        <a
                          {...props}
                          target={'_blank'}
                          rel={'noreferrer noopener'}
                        />
                      )
                    }
                  },
                  ul: {
                    component: function MarkdownUnorderedList(
                      props: React.ComponentProps<'ul'>
                    ) {
                      return <CollapsibleList {...props} ordered={false} />
                    }
                  },
                  ol: {
                    component: function MarkdownOrderedList(
                      props: React.ComponentProps<'ol'>
                    ) {
                      return <CollapsibleList {...props} ordered={true} />
                    }
                  }
                }
              }}
            >
              {textToRender}
            </Markdown>
          )}
          {documents && documents.length > 0 && (
            <div className='documents'>
              <div className='documents-label'>Documents Uploaded:</div>
              <div className='chip-container'>
                {documents.map((document, i) => {
                  return (
                    <Chip key={document.documentId || i} variant={'primary'}>
                      {document.fileName}
                    </Chip>
                  )
                })}
              </div>
            </div>
          )}
        </div>
        {type === 'user' && <span className='icon'>{initials}</span>}
      </li>
    </>
  )
}

export default ChatMessage
